import { Trans, useTranslation } from 'react-i18next';
import { Box, Button, Link, Stack, Typography } from '@mui/material';
import { Logo } from '@/components/layout/components/logo';
import { ErrorCode } from '@/constants/error';
import { Flow } from '@/constants/flow';
import { useBizState } from '@/hooks/use-biz-state';
import { useCustom } from '@/hooks/use-custom';
import { useFlow } from '@/hooks/use-flow';
import { MySvgIcon } from '../../ui/svg-icon';

interface ExceptionProps {
  iconName: string;
  title: string;
  description?: React.ReactNode;
  action?: React.ReactNode;
}

const RefreshButton = () => {
  const { t } = useTranslation();
  return (
    <Button variant="contained" onClick={() => window.location.reload()}>
      {t('refresh')}
    </Button>
  );
};

const Exception: React.FC<ExceptionProps> = ({ iconName, title, description, action }) => {
  const { layoutConfig } = useCustom();
  const showLogo = layoutConfig?.layout !== 'space-between';
  return (
    <Box
      sx={(theme) => ({
        padding: { sm: theme.spacing(6), xs: theme.spacing(3) },
      })}
    >
      {showLogo && <Logo />}

      <Stack direction="column" justifyContent="center" alignItems="center" spacing={2}>
        <MySvgIcon name={iconName} width="300" height="225" />
        <Typography variant="h6" className="text-center">
          {title}
        </Typography>
        <Typography className="text-center" component="div">
          {description}
        </Typography>
        {action}
      </Stack>
    </Box>
  );
};

export const ExceptionUnknownError = () => {
  const { t } = useTranslation();
  const { errorCode } = useBizState();
  const isMissingParameters = errorCode === ErrorCode.MissingParameters;

  if (isMissingParameters) {
    return <Exception iconName="wrong" title={t('session_expired')} description={t('session_expired_description')} />;
  }

  return (
    <Exception
      iconName="wrong"
      title={t('something_went_wrong')}
      description={t('refresh_suggestion_message')}
      action={<RefreshButton />}
    />
  );
};

export const Exception404 = () => {
  const { t } = useTranslation();
  const { setFlow } = useFlow();
  return (
    <Exception
      iconName="404"
      title={t('page_not_found_title')}
      description={
        <Stack direction="row" justifyContent="center" alignItems="center" flexWrap="wrap">
          <Trans i18nKey="page_not_found_description">
            <Link
              underline="hover"
              component="button"
              onClick={() => setFlow(Flow.LOGIN_STEP_INPUT_USERNAME)}
              sx={{ pl: 1 }}
            ></Link>
          </Trans>
        </Stack>
      }
    />
  );
};

export const ExceptionCookieBlocked = () => {
  const { t } = useTranslation();
  return (
    <Exception
      iconName="cookie-blocked"
      title={t('cookie_blocked_error_title')}
      description={t('cookie_check_instruction')}
      action={<RefreshButton />}
    />
  );
};

export const ExceptionRequestTimeout = () => {
  const { t } = useTranslation();
  return (
    <Exception
      iconName="wrong"
      title={t('request_timed_out')}
      description={t('request_timed_out_description')}
      action={<RefreshButton />}
    />
  );
};

export const ExceptionNetworkError = () => {
  const { t } = useTranslation();
  return (
    <Exception
      iconName="wrong"
      title={t('network_error')}
      description={t('network_error_description')}
      action={<RefreshButton />}
    />
  );
};
